import React, { useState, useEffect } from 'react';
import GestorLayout from '../../components/GestorLayout';
import axios from 'axios';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { 
  Users, Plus, Edit2, Trash2, Shield, UserCog, 
  Eye, EyeOff, Check, X, Settings, Save,
  LayoutDashboard, CalendarDays, Package, AlertTriangle, RefreshCw,
  BarChart3, DollarSign, Tv, Building2, Layers, Bell, Wrench
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Lista de todos os menus disponíveis
const MENUS_DISPONIVEIS = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'planeamento', label: 'Planeamento', icon: CalendarDays },
  { id: 'ofs', label: 'Ordens de Fabrico', icon: Package },
  { id: 'anomalias', label: 'Anomalias', icon: AlertTriangle },
  { id: 'resgates', label: 'Resgates e Arranjos', icon: RefreshCw },
  { id: 'relatorios', label: 'Relatórios', icon: BarChart3 },
  { id: 'financeiro', label: 'Financeiro', icon: DollarSign },
  { id: 'estado-operadores', label: 'Estado Operadores', icon: Eye },
  { id: 'tv', label: 'TV Dashboard', icon: Tv },
  { id: 'operadores', label: 'Operadores', icon: Users },
  { id: 'marcas', label: 'Marcas', icon: Building2 },
  { id: 'operacoes', label: 'Operações', icon: Layers },
  { id: 'configuracoes', label: 'Configurações', icon: Settings },
  { id: 'alertas', label: 'Alertas', icon: Bell },
  { id: 'sistemas', label: 'Sistemas', icon: Wrench },
  { id: 'utilizadores', label: 'Utilizadores', icon: Shield },
];

export default function GestaoUtilizadores() {
  const [utilizadores, setUtilizadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [gestorPermissoes, setGestorPermissoes] = useState([]);
  const [savingPermissoes, setSavingPermissoes] = useState(false);
  
  const [formData, setFormData] = useState({
    username: '',
    nome_completo: '',
    role: 'gestor',
    senha: ''
  });

  useEffect(() => {
    loadUtilizadores();
    loadPermissoes();
  }, []);

  const loadPermissoes = async () => {
    try {
      const response = await axios.get(`${API}/configuracoes/gestor-permissoes`);
      setGestorPermissoes(response.data.menus || MENUS_DISPONIVEIS.map(m => m.id));
    } catch (error) {
      // Se não existir, usar todos os menus por defeito
      setGestorPermissoes(MENUS_DISPONIVEIS.map(m => m.id));
    }
  };

  const savePermissoes = async () => {
    setSavingPermissoes(true);
    try {
      await axios.put(`${API}/configuracoes/gestor-permissoes`, { menus: gestorPermissoes });
      toast.success('Permissões do Gestor guardadas!');
    } catch (error) {
      toast.error('Erro ao guardar permissões');
    } finally {
      setSavingPermissoes(false);
    }
  };

  const togglePermissao = (menuId) => {
    setGestorPermissoes(prev => 
      prev.includes(menuId) 
        ? prev.filter(id => id !== menuId)
        : [...prev, menuId]
    );
  };

  const loadUtilizadores = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/utilizadores`);
      setUtilizadores(response.data);
    } catch (error) {
      console.error('Erro ao carregar utilizadores:', error);
      toast.error('Erro ao carregar utilizadores');
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      username: '',
      nome_completo: '',
      role: 'gestor',
      senha: ''
    });
    setShowDialog(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      username: user.username,
      nome_completo: user.nome_completo || '',
      role: user.role || 'gestor',
      senha: ''
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!formData.username.trim()) {
      toast.error('Username obrigatório');
      return;
    }
    
    setSaving(true);
    try {
      if (editingUser) {
        // Atualizar
        await axios.put(`${API}/utilizadores/${editingUser.id}`, {
          nome_completo: formData.nome_completo,
          role: formData.role,
          senha: formData.senha || undefined
        });
        toast.success('Utilizador atualizado!');
      } else {
        // Criar
        if (!formData.senha) {
          toast.error('Senha obrigatória para novo utilizador');
          setSaving(false);
          return;
        }
        await axios.post(`${API}/utilizadores`, formData);
        toast.success('Utilizador criado!');
      }
      setShowDialog(false);
      loadUtilizadores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleAtivo = async (user) => {
    const novoEstado = user.ativo === false ? true : false;
    const acao = novoEstado ? 'ativar' : 'desativar';
    
    if (!window.confirm(`Tem certeza que deseja ${acao} o utilizador "${user.username}"?`)) {
      return;
    }
    
    try {
      await axios.put(`${API}/utilizadores/${user.id}`, { ativo: novoEstado });
      toast.success(`Utilizador ${novoEstado ? 'ativado' : 'desativado'}!`);
      loadUtilizadores();
    } catch (error) {
      toast.error(`Erro ao ${acao} utilizador`);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Tem certeza que deseja eliminar o utilizador "${user.username}"?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/utilizadores/${user.id}`);
      toast.success('Utilizador eliminado!');
      loadUtilizadores();
    } catch (error) {
      toast.error('Erro ao eliminar');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'gestor': return 'bg-blue-100 text-blue-700';
      default: return 'bg-stone-100 text-stone-700';
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin': return <Shield className="h-4 w-4" />;
      case 'gestor': return <UserCog className="h-4 w-4" />;
      default: return <Users className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <GestorLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </GestorLayout>
    );
  }

  return (
    <GestorLayout>
      <div className="space-y-6" data-testid="gestao-utilizadores-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-stone-900">Gestão de Utilizadores</h1>
            <p className="text-stone-600">Gerir acessos de administradores e gestores</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="btn-novo-utilizador">
            <Plus className="h-4 w-4 mr-2" /> Novo Utilizador
          </Button>
        </div>

        {/* Informação sobre roles */}
        <div className="grid grid-cols-2 gap-4">
          <Card className="p-4 border-l-4 border-l-red-500">
            <div className="flex items-center gap-3">
              <Shield className="h-8 w-8 text-red-500" />
              <div>
                <h3 className="font-semibold text-stone-900">Admin</h3>
                <p className="text-sm text-stone-500">Acesso total a todas as funcionalidades</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 border-l-4 border-l-blue-500">
            <div className="flex items-center gap-3">
              <UserCog className="h-8 w-8 text-blue-500" />
              <div>
                <h3 className="font-semibold text-stone-900">Gestor</h3>
                <p className="text-sm text-stone-500">Acesso configurável aos menus abaixo</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Painel de Permissões do Gestor */}
        <Card className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Settings className="h-5 w-5 text-blue-600" />
              <h3 className="font-semibold text-stone-900">Permissões do Perfil Gestor</h3>
            </div>
            <Button 
              onClick={savePermissoes} 
              disabled={savingPermissoes}
              size="sm"
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {savingPermissoes ? 'A guardar...' : 'Guardar Permissões'}
            </Button>
          </div>
          <p className="text-sm text-stone-500 mb-4">
            Selecione os menus que os utilizadores com perfil "Gestor" podem aceder:
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {MENUS_DISPONIVEIS.map((menu) => {
              const IconComponent = menu.icon;
              const isSelected = gestorPermissoes.includes(menu.id);
              return (
                <div
                  key={menu.id}
                  onClick={() => togglePermissao(menu.id)}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    isSelected 
                      ? 'border-blue-500 bg-blue-50' 
                      : 'border-stone-200 bg-white hover:border-stone-300'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    isSelected ? 'bg-blue-500 text-white' : 'bg-stone-100 text-stone-500'
                  }`}>
                    <IconComponent className="h-4 w-4" />
                  </div>
                  <span className={`text-sm font-medium ${isSelected ? 'text-blue-700' : 'text-stone-600'}`}>
                    {menu.label}
                  </span>
                  {isSelected && <Check className="h-4 w-4 text-blue-500 ml-auto" />}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Lista de Utilizadores */}
        <Card>
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold">Utilizadores ({utilizadores.length})</h2>
          </div>
          <div className="divide-y">
            {utilizadores.length === 0 ? (
              <div className="p-8 text-center text-stone-500">
                Nenhum utilizador encontrado
              </div>
            ) : (
              utilizadores.map((user) => (
                <div key={user.id} data-testid={`user-row-${user.username}`} className="p-4 flex items-center justify-between hover:bg-stone-50">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${getRoleColor(user.role)}`}>
                      {getRoleIcon(user.role)}
                    </div>
                    <div>
                      <p className="font-semibold text-stone-900">{user.nome_completo || user.username}</p>
                      <p className="text-sm text-stone-500">@{user.username}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getRoleColor(user.role)}`}>
                      {user.role === 'admin' ? 'Administrador' : 'Gestor'}
                    </span>
                    <button 
                      onClick={() => handleToggleAtivo(user)}
                      className={`px-2 py-1 rounded text-xs cursor-pointer transition-colors ${user.ativo !== false ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                      title={user.ativo !== false ? 'Clique para desativar' : 'Clique para ativar'}
                    >
                      {user.ativo !== false ? 'Ativo' : 'Inativo'}
                    </button>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(user)} data-testid={`btn-edit-${user.username}`}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(user)} data-testid={`btn-delete-${user.username}`} className="text-red-500 hover:text-red-700 hover:bg-red-50">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </Card>

        {/* Dialog Criar/Editar */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Editar Utilizador' : 'Novo Utilizador'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Username</label>
                <Input
                  data-testid="input-username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  disabled={!!editingUser}
                  placeholder="admin"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Nome Completo</label>
                <Input
                  data-testid="input-nome-completo"
                  value={formData.nome_completo}
                  onChange={(e) => setFormData({ ...formData, nome_completo: e.target.value })}
                  placeholder="João Silva"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Tipo de Acesso</label>
                <Select value={formData.role} onValueChange={(val) => setFormData({ ...formData, role: val })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o tipo de acesso" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">
                      <div className="flex items-center gap-2">
                        <Shield className="h-4 w-4 text-red-500" />
                        <span>Administrador (acesso total)</span>
                      </div>
                    </SelectItem>
                    <SelectItem value="gestor">
                      <div className="flex items-center gap-2">
                        <UserCog className="h-4 w-4 text-blue-500" />
                        <span>Gestor (acesso limitado)</span>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">
                  {editingUser ? 'Nova Senha (deixe vazio para manter)' : 'Senha'}
                </label>
                <div className="relative">
                  <Input
                    data-testid="input-senha"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.senha}
                    onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                    placeholder={editingUser ? '••••••••' : 'admin123'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setShowDialog(false)} data-testid="btn-cancel-user">Cancelar</Button>
              <Button onClick={handleSave} disabled={saving} data-testid="btn-save-user">
                {saving ? 'A guardar...' : (editingUser ? 'Atualizar' : 'Criar')}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </GestorLayout>
  );
}
